import {
  ACTIVITY_STATUSES,
  CONTROL_PLANE_SKILL_PLAN_STATES,
  CONTROL_PLANE_CONNECTION_STATES,
  CONTROL_PLANE_EXPIRY_BANDS,
  ONBOARDING_PUBLIC_ERROR_CODES,
  PHASES_ORDER,
  WORKSPACE_CLEANUP_STATES,
  isOnboardingAttemptState,
  isOnboardingBootstrapActionCode,
  isOnboardingBootstrapFailureCode,
  isOnboardingBootstrapStage,
  isOnboardingPairingStatus,
  isPairingPurpose,
  isSessionObservabilityProjection,
  type ControlPlaneCapability,
  type ControlPlaneManagementResponse,
  type ControlPlaneSkillPlanProjection,
  type OnboardingPublicError,
  type ResumeOnboardingAttemptResponse,
} from '@internal/spassu-code-contract';

type JsonObject = Record<string, unknown>;

function hasOnlyKeys(row: JsonObject, allowed: readonly string[]): boolean {
  return Object.keys(row).every(key => allowed.includes(key));
}

function object(value: unknown): JsonObject | null {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    ? (value as JsonObject)
    : null;
}

function stringOrNull(value: unknown): value is string | null {
  return value === null || typeof value === 'string';
}

function timestampOrNull(value: unknown): value is string | null {
  return value === null || validTimestamp(value);
}

function validTimestamp(value: unknown): value is string {
  return typeof value === 'string' && Number.isFinite(Date.parse(value));
}

function capability(value: unknown): value is ControlPlaneCapability {
  const row = object(value);
  return Boolean(
    row &&
      typeof row.allowed === 'boolean' &&
      stringOrNull(row.reasonCode) &&
      stringOrNull(row.reason),
  );
}

export function decodeControlPlaneSkillPlanProjection(
  value: unknown,
): ControlPlaneSkillPlanProjection {
  const row = object(value);
  if (
    !row ||
    !hasOnlyKeys(row, ['phase', 'state', 'required', 'optionalCount']) ||
    !PHASES_ORDER.includes(row.phase as never) ||
    !CONTROL_PLANE_SKILL_PLAN_STATES.includes(row.state as never) ||
    !Array.isArray(row.required) ||
    !Number.isInteger(row.optionalCount) ||
    Number(row.optionalCount) < 0
  ) {
    throw new Error('A resposta de skills da atividade é inválida.');
  }
  const validItems = row.required.every(itemValue => {
    const item = object(itemValue);
    return Boolean(
      item &&
        hasOnlyKeys(item, [
          'name',
          'description',
          'canonicalAlias',
          'version',
          'origin',
          'scopeType',
        ]) &&
        typeof item.name === 'string' &&
        stringOrNull(item.description) &&
        stringOrNull(item.canonicalAlias) &&
        typeof item.version === 'string' &&
        ['internal', 'community'].includes(String(item.origin)) &&
        ['corporate', 'project'].includes(String(item.scopeType)),
    );
  });
  if (!validItems) {
    throw new Error('A resposta de skills da atividade é inválida.');
  }
  return value as ControlPlaneSkillPlanProjection;
}

function capabilities(value: unknown): boolean {
  const row = object(value);
  if (!row) return false;
  const required = [
    'pair',
    'renew',
    'recover',
    'resume',
    'openCockpit',
    'finalize',
    'cancel',
    'selectForDeletion',
    'overrideCleanup',
    'enrollCleanup',
  ];
  return (
    required.every(key => capability(row[key])) &&
    (row.recoverUsage === undefined || capability(row.recoverUsage))
  );
}

function attempt(value: unknown): boolean {
  if (value === null || value === undefined) return true;
  const row = object(value);
  if (
    !row ||
    !hasOnlyKeys(row, [
      'sessionId',
      'purpose',
      'state',
      'stage',
      'stageChangedAt',
      'failedAt',
      'diagnostic',
    ]) ||
    typeof row.sessionId !== 'string' ||
    !isPairingPurpose(row.purpose) ||
    row.purpose === 'cleanup-enrollment' ||
    !isOnboardingAttemptState(row.state) ||
    !isOnboardingBootstrapStage(row.stage) ||
    !validTimestamp(row.stageChangedAt) ||
    !timestampOrNull(row.failedAt)
  ) {
    return false;
  }
  if (row.diagnostic === null) return true;
  const diagnostic = object(row.diagnostic);
  return Boolean(
    diagnostic &&
      hasOnlyKeys(diagnostic, ['code', 'actionCode']) &&
      isOnboardingBootstrapFailureCode(diagnostic.code) &&
      isOnboardingBootstrapActionCode(diagnostic.actionCode),
  );
}

function projection(value: unknown): boolean {
  const row = object(value);
  const connection = object(row?.connection);
  const workspace = object(row?.workspace);
  return Boolean(
    row &&
      typeof row.activityId === 'string' &&
      ACTIVITY_STATUSES.some(status => status === row.activityCycle) &&
      connection &&
      CONTROL_PLANE_CONNECTION_STATES.some(
        state => state === connection.state,
      ) &&
      CONTROL_PLANE_EXPIRY_BANDS.some(band => band === connection.expiryBand) &&
      timestampOrNull(connection.expiresAt) &&
      (connection.remainingMs === null ||
        typeof connection.remainingMs === 'number') &&
      timestampOrNull(connection.lastConfirmedAt) &&
      stringOrNull(connection.sessionId) &&
      stringOrNull(connection.credentialId) &&
      attempt(row.attempt) &&
      workspace &&
      WORKSPACE_CLEANUP_STATES.some(state => state === workspace.state) &&
      capabilities(row.capabilities) &&
      (row.sessionObservability === undefined ||
        isSessionObservabilityProjection(row.sessionObservability)) &&
      validTimestamp(row.observedAt) &&
      typeof row.stale === 'boolean',
  );
}

export function decodeControlPlaneManagementResponse(
  value: unknown,
): ControlPlaneManagementResponse {
  const row = object(value);
  const activity = object(row?.activity);
  if (
    !row ||
    !activity ||
    typeof activity.id !== 'string' ||
    typeof activity.title !== 'string' ||
    activity.workPath !== 'control-plane' ||
    !projection(row.projection)
  ) {
    throw new Error('A resposta de gestão da conexão é inválida.');
  }
  return value as ControlPlaneManagementResponse;
}

export function decodeResumeOnboardingAttemptResponse(
  value: unknown,
): ResumeOnboardingAttemptResponse {
  const row = object(value);
  if (
    !row ||
    !hasOnlyKeys(row, [
      'id',
      'activityId',
      'status',
      'expiresAt',
      'createdAt',
      'codeAvailable',
      'pairingCode',
      'terminalCommand',
      'purpose',
    ]) ||
    typeof row.id !== 'string' ||
    typeof row.activityId !== 'string' ||
    !isOnboardingPairingStatus(row.status) ||
    !validTimestamp(row.expiresAt) ||
    !validTimestamp(row.createdAt) ||
    typeof row.codeAvailable !== 'boolean' ||
    (row.purpose !== undefined && !isPairingPurpose(row.purpose))
  ) {
    throw new Error('A resposta de retomada da conexão é inválida.');
  }
  if (
    row.codeAvailable &&
    (typeof row.pairingCode !== 'string' ||
      typeof row.terminalCommand !== 'string')
  ) {
    throw new Error('A resposta de retomada da conexão é inválida.');
  }
  if (
    !row.codeAvailable &&
    (row.pairingCode !== undefined || row.terminalCommand !== undefined)
  ) {
    throw new Error('A resposta de retomada da conexão é inválida.');
  }
  return value as ResumeOnboardingAttemptResponse;
}

export function decodeOnboardingPublicError(
  value: unknown,
): Partial<OnboardingPublicError> | undefined {
  const row = object(value);
  if (!row) return undefined;
  const keys = Object.keys(row);
  if (
    !keys.every(key => ['code', 'message', 'action'].includes(key)) ||
    !ONBOARDING_PUBLIC_ERROR_CODES.some(code => code === row.code) ||
    typeof row.message !== 'string' ||
    (row.action !== undefined && typeof row.action !== 'string')
  ) {
    return undefined;
  }
  return row as unknown as Partial<OnboardingPublicError>;
}
